import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AuthService, ApiResponse } from './auth.service';
import { Permission, UserPermissions, userHasAnyPermission } from './permissions';
import { environment } from '../../environments/environment';

export interface ExperimentalFeatureDef {
  id: string;
  labelKey: string;
  descriptionKey: string;
  /**
   * Feature permissions the experiment sits on top of — ANY of them is enough. An experiment is a
   * beta switch for something the user is *already* allowed to use, never a way in: if the user
   * doesn't hold one of these, the whole row is hidden (see `visibleFeatures`) because flipping it
   * would produce no visible change. Omit for experiments every user can see the effect of.
   */
  requiredPermissions?: readonly (Permission | string)[];
}

// Hardcoded for now; a future iteration can move this to a backend-driven list.
// The backend deliberately does NOT mirror this list: it validates the shape of a feature ID and
// stores whatever it is given, so adding an experiment stays a frontend-only change.
export const EXPERIMENTAL_FEATURES: ExperimentalFeatureDef[] = [
  {
    id: 'audienceOwnerFilter',
    labelKey: 'experimentalFeatures.features.audienceOwnerFilter.label',
    descriptionKey: 'experimentalFeatures.features.audienceOwnerFilter.description',
    requiredPermissions: [UserPermissions.REACH_AUDIENCE]
  },
  {
    id: 'accordionProductsMenu',
    labelKey: 'experimentalFeatures.features.accordionProductsMenu.label',
    descriptionKey: 'experimentalFeatures.features.accordionProductsMenu.description'
  },
  {
    id: 'audienceInsights',
    labelKey: 'experimentalFeatures.features.audienceInsights.label',
    descriptionKey: 'experimentalFeatures.features.audienceInsights.description',
    requiredPermissions: [UserPermissions.ADMIN_DASHBOARD]
  }
];

/**
 * Opt-ins live on the *user*, server-side, and ride along with the session payload like
 * `permissions` do.
 *
 * They used to be a localStorage key, which made them a property of the browser: they were wiped
 * by logout (which clears the session storage) and they leaked into impersonation, showing an
 * admin a UI the impersonated user could never actually get. Both are the same bug — wrong scope —
 * so the fix is the scope, not two patches. Do not reintroduce a localStorage fallback here: it
 * would bring the impersonation leak back with it.
 */
@Injectable({ providedIn: 'root' })
export class ExperimentalFeaturesService {
  readonly features = EXPERIMENTAL_FEATURES;

  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly endpoint = `${environment.apiUrl.replace(/\/+$/u, '')}/webapp/auth/experimental-features`;

  readonly enabled$: Observable<Record<string, boolean>> = this.authService.currentUser$.pipe(
    map(user => user?.experimentalFeatures ?? {}),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  /**
   * The experiments this user could actually observe the effect of. An experiment whose underlying
   * feature permission the user lacks is hidden rather than shown-and-disabled: a switch that is
   * inert no matter which way you flip it is noise, not information.
   */
  get visibleFeatures(): ExperimentalFeatureDef[] {
    const permissions = this.authService.currentUserValue?.permissions;
    return this.features.filter(feature => userHasAnyPermission(permissions, feature.requiredPermissions ?? []));
  }

  /**
   * Permission-gated on purpose: a flag can outlive the permission that made it meaningful (toggled
   * on, then the permission is revoked), and the stored `true` must not resurface as an enabled
   * experiment. Call sites may still AND in their own, narrower checks.
   */
  isEnabled(id: string): boolean {
    if (!this.isPermitted(id)) {
      return false;
    }
    return !!this.authService.currentUserValue?.experimentalFeatures?.[id];
  }

  isEnabled$(id: string): Observable<boolean> {
    return this.authService.currentUser$.pipe(
      map(user => this.isPermitted(id, user?.permissions) && !!user?.experimentalFeatures?.[id]),
      distinctUntilChanged()
    );
  }

  private isPermitted(id: string, permissions = this.authService.currentUserValue?.permissions): boolean {
    const feature = this.features.find(f => f.id === id);
    // Unknown IDs (a stale flag from a removed experiment) carry no requirement to check.
    return userHasAnyPermission(permissions, feature?.requiredPermissions ?? []);
  }

  /**
   * Optimistic: the toggle flips immediately, then the server's resulting state replaces it —
   * which also rolls the switch back if the write was rejected (e.g. the permission was revoked
   * since the panel was opened).
   */
  setEnabled(id: string, value: boolean): void {
    // The panel never renders these rows, so this only catches a programmatic caller.
    if (!this.isPermitted(id)) {
      console.warn(`[experimental-features] Ignored toggle of "${id}": missing the required permission.`);
      return;
    }

    const previous = this.authService.currentUserValue?.experimentalFeatures ?? {};
    this.authService.applyExperimentalFeatures({ ...previous, [id]: value });

    this.http.post<ApiResponse<Record<string, boolean>>>(
      this.endpoint,
      { FeatureID: id, Enabled: value },
      { withCredentials: true }
    ).pipe(
      catchError(error => {
        console.error('[experimental-features] Failed to persist toggle:', error);
        this.authService.applyExperimentalFeatures(previous);
        return of(null);
      })
    ).subscribe(response => {
      const state = response?.payload;
      if (state) {
        this.authService.applyExperimentalFeatures(state);
      }
    });
  }

  /**
   * Re-read from the server. The session payload is authoritative at login, but goes stale if the
   * user toggles a flag in another tab or on another device — so the panel refreshes on open.
   */
  refresh(): void {
    this.http.get<ApiResponse<Record<string, boolean>>>(this.endpoint, { withCredentials: true })
      .pipe(
        tap(response => {
          const state = response?.payload;
          if (state) {
            this.authService.applyExperimentalFeatures(state);
          }
        }),
        catchError(error => {
          // Non-fatal: the session payload we already have stays in effect.
          console.error('[experimental-features] Failed to refresh flags:', error);
          return of(null);
        })
      )
      .subscribe();
  }
}
